import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LanSettings } from 'types/common';
import { mergeSettings } from 'config/config';
import settingsService from '@/services/settingsService';

interface SettingsContextType {
    settings: LanSettings | null;
    loading: boolean;
    error: string | null;
    refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
    children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
    const [settings, setSettings] = useState<LanSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const apiSettings = await settingsService.getSettings();
            const mergedSettings = mergeSettings(apiSettings);
            setSettings(mergedSettings);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch settings, using defaults:', err);
            setError('Failed to load settings');
            // Use fallback settings when API fails
            const fallbackSettings = mergeSettings(null);
            setSettings(fallbackSettings);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const refreshSettings = async () => {
        await fetchSettings();
    };
    return (
        <SettingsContext.Provider value={{ settings, loading, error, refreshSettings }}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettingsContext = () => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettingsContext must be used within a SettingsProvider');
    }
    return context;
};
